import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  ArrowRight, Check, ShieldCheck, FileSignature, Sparkles, Stamp, MailCheck,
  ChevronRight, HelpCircle, Scale, Clock, Lock, ArrowUpRight, AlertTriangle,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { getConfig } from "@/lib/api";
import {
  getNoticeBySlug, relatedNotices, HOW_IT_WORKS, WHY_ZEXO, SLUG_BY_TYPE, categorySlug,
} from "@/lib/seoNotices";
import { getNoticeGuide } from "@/lib/noticeGuides";

const SITE_URL = process.env.REACT_APP_SITE_URL || process.env.REACT_APP_BACKEND_URL || "";
const STEP_ICONS = [FileSignature, Sparkles, Stamp, MailCheck];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

export default function NoticeLandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const notice = getNoticeBySlug(slug);
  const [config, setConfig] = useState({ firm_name: "Zexo Legal", price: 249 });

  useEffect(() => {
    if (!notice) navigate("/legal-notices", { replace: true });
    window.scrollTo(0, 0);
  }, [slug, notice, navigate]);

  useEffect(() => { getConfig().then(setConfig).catch(() => {}); }, []);

  if (!notice) return null;

  const price = config.price || 249;
  const related = relatedNotices(slug);
  const rg = getNoticeGuide(slug);
  const faqs = rg ? [...notice.faqs, ...rg.extraFaqs] : notice.faqs;
  const canonical = `${SITE_URL}/legal-notice/${slug}`;

  const faqSchema = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question", name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const serviceSchema = {
    "@context": "https://schema.org", "@type": "Service",
    name: notice.title, serviceType: "Legal notice drafting and dispatch",
    provider: { "@type": "LegalService", name: config.firm_name || "Zexo Legal", url: SITE_URL },
    areaServed: "IN", description: notice.metaDescription,
    offers: { "@type": "Offer", price: String(price), priceCurrency: "INR", url: canonical },
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Legal Notices", item: `${SITE_URL}/legal-notices` },
      { "@type": "ListItem", position: 3, name: notice.title, item: canonical },
    ],
  };

  const startHref = { pathname: "/create" };
  const goStart = () => navigate("/create", { state: { preselect: notice.typeId } });

  return (
    <div className="min-h-screen bg-cream text-ink">
      <Helmet>
        <title>{notice.metaTitle}</title>
        <meta name="description" content={notice.metaDescription} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={notice.metaTitle} />
        <meta property="og:description" content={notice.metaDescription} />
        <meta property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={notice.metaTitle} />
        <meta name="twitter:description" content={notice.metaDescription} />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <SiteHeader firmName={config.firm_name} />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="mx-auto max-w-5xl px-6 pt-6 lg:px-10" data-testid="breadcrumb">
        <ol className="flex flex-wrap items-center gap-1.5 text-xs font-semibold text-slate-500">
          <li><Link to="/" className="hover:text-navy">Home</Link></li>
          <ChevronRight className="h-3.5 w-3.5" />
          <li><Link to="/legal-notices" className="hover:text-navy">Legal Notices</Link></li>
          <ChevronRight className="h-3.5 w-3.5" />
          <li><Link to={`/legal-notices/${categorySlug(notice.category)}`} className="hover:text-navy" data-testid="breadcrumb-category">{notice.categoryTitle}</Link></li>
        </ol>
      </nav>

      {/* HERO */}
      <section className="mx-auto max-w-5xl px-6 pb-10 pt-6 lg:px-10">
        <motion.div variants={fadeUp} initial="hidden" animate="show">
          <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-goldDark">
            <ShieldCheck className="h-3.5 w-3.5" /> Advocate-verified before it's sent
          </span>
          <h1 data-testid="notice-h1" className="mt-5 font-display text-4xl font-black leading-tight text-ink sm:text-5xl lg:text-6xl">
            {notice.title}
          </h1>
          <p className="mt-5 max-w-3xl text-base leading-relaxed text-slate-600 sm:text-lg">{notice.intro}</p>
          <div className="mt-8 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
            <button data-testid="hero-generate-cta" onClick={goStart}
              className="inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-navy/30 transition-transform duration-200 hover:-translate-y-0.5 active:scale-95">
              Generate Legal Notice <ArrowRight className="h-4 w-4" />
            </button>
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500">
              <Clock className="h-4 w-4 text-sage" /> Ready in minutes · from ₹{price}
            </span>
          </div>
        </motion.div>
      </section>

      {notice.guide && (
        <section className="mx-auto max-w-5xl px-6 pb-4 lg:px-10" data-testid="guide-what-is">
          <h2 className="font-display text-2xl font-extrabold text-ink">What is this notice?</h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600">{notice.guide.whatIsIt}</p>
        </section>
      )}

      {/* WHEN USED */}
      <section className="mx-auto max-w-5xl px-6 py-8 lg:px-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <h2 className="font-display text-2xl font-extrabold text-ink">When is this notice used?</h2>
          </div>
          <div className="lg:col-span-2">
            <p className="text-base leading-relaxed text-slate-600">{notice.whenUsed}</p>
            <ul className="mt-6 grid grid-cols-1 gap-3" data-testid="situations-list">
              {notice.situations.map((s, i) => (
                <li key={i} className="flex items-start gap-3 rounded-2xl border border-border bg-white p-4">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sage/15 text-sage"><Check className="h-4 w-4" strokeWidth={3} /></span>
                  <span className="text-sm leading-relaxed text-ink">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {notice.guide && (
        <>
          <section className="mx-auto max-w-5xl px-6 py-8 lg:px-10" data-testid="guide-legal-grounds">
            <div className="flex items-center gap-2 text-goldDark">
              <Scale className="h-4 w-4" />
              <span className="text-xs font-bold uppercase tracking-[0.18em]">Legal grounds</span>
            </div>
            <h2 className="mt-2 font-display text-2xl font-extrabold text-ink">The law behind this notice</h2>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {notice.guide.legalGrounds.map((g, i) => (
                <div key={i} className="rounded-2xl border border-border bg-white p-5">
                  <p className="font-display text-sm font-bold text-navy">{g.section}</p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{g.text}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mx-auto max-w-5xl px-6 py-8 lg:px-10" data-testid="guide-documents">
            <h2 className="font-display text-2xl font-extrabold text-ink">Documents &amp; information you'll need</h2>
            <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {notice.guide.documentsRequired.map((d, i) => (
                <li key={i} className="flex items-start gap-3 rounded-2xl border border-border bg-white p-4">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-navy/10 text-navy"><FileSignature className="h-3.5 w-3.5" /></span>
                  <span className="text-sm leading-relaxed text-ink">{d}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 flex items-start gap-2 rounded-xl border border-gold/40 bg-gold/10 px-4 py-3 text-sm text-goldDark"><Sparkles className="mt-0.5 h-4 w-4 shrink-0" /> {notice.guide.wizardNote}</p>
          </section>
        </>
      )}

      {rg && (
        <>
          <section className="mx-auto max-w-5xl px-6 py-8 lg:px-10" data-testid="guide-before-send">
            <div className="flex items-center gap-2 text-goldDark"><ShieldCheck className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-[0.18em]">Before you send this notice</span></div>
            <h2 className="mt-2 font-display text-2xl font-extrabold text-ink">Get this right before you send</h2>
            <p className="mt-3 text-base leading-relaxed text-slate-600">{rg.beforeYouSend.intro}</p>
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-border bg-white p-5">
                <h3 className="font-display text-sm font-bold text-navy">Checklist before sending</h3>
                <ul className="mt-3 space-y-2">{rg.beforeYouSend.checklist.map((x, i) => (<li key={i} className="flex items-start gap-2 text-sm text-ink"><Check className="mt-0.5 h-4 w-4 shrink-0 text-sage" strokeWidth={3} />{x}</li>))}</ul>
              </div>
              <div className="rounded-2xl border border-border bg-white p-5">
                <h3 className="font-display text-sm font-bold text-navy">Common prerequisites</h3>
                <ul className="mt-3 space-y-2">{rg.beforeYouSend.prerequisites.map((x, i) => (<li key={i} className="flex items-start gap-2 text-sm text-ink"><ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-goldDark" />{x}</li>))}</ul>
              </div>
              <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-5">
                <h3 className="font-display text-sm font-bold text-destructive">When a notice may not be appropriate</h3>
                <ul className="mt-3 space-y-2">{rg.beforeYouSend.notAppropriate.map((x, i) => (<li key={i} className="flex items-start gap-2 text-sm text-ink"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />{x}</li>))}</ul>
              </div>
              <div className="rounded-2xl border border-gold/40 bg-gold/10 p-5">
                <h3 className="font-display text-sm font-bold text-goldDark">Practical tips</h3>
                <ul className="mt-3 space-y-2">{rg.beforeYouSend.tips.map((x, i) => (<li key={i} className="flex items-start gap-2 text-sm text-ink"><Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-goldDark" />{x}</li>))}</ul>
              </div>
            </div>
          </section>

          <section className="mx-auto max-w-5xl px-6 py-8 lg:px-10" data-testid="guide-timeline">
            <div className="flex items-center gap-2 text-goldDark"><Clock className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-[0.18em]">Legal timeline</span></div>
            <h2 className="mt-2 font-display text-2xl font-extrabold text-ink">How the process unfolds — step by step</h2>
            <ol className="mt-6 space-y-4">
              {rg.timeline.map((t, i) => (
                <li key={i} className="flex gap-4">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-navy font-display text-sm font-bold text-white">{i + 1}</span>
                  <div className="rounded-2xl border border-border bg-white p-4 flex-1">
                    <div className="flex flex-wrap items-center justify-between gap-2"><p className="font-display text-sm font-bold text-ink">{t.stage}</p><span className="rounded-full bg-cream px-3 py-0.5 text-xs font-semibold text-navy">{t.when}</span></div>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">{t.detail}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <section className="mx-auto max-w-5xl px-6 py-8 lg:px-10" data-testid="guide-rights">
            <div className="flex items-center gap-2 text-goldDark"><Scale className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-[0.18em]">Your rights &amp; their obligations</span></div>
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-border bg-white p-5"><h3 className="font-display text-sm font-bold text-sage">Rights of the sender</h3><ul className="mt-3 space-y-2">{rg.rights.sender.map((x, i) => (<li key={i} className="flex items-start gap-2 text-sm text-ink"><Check className="mt-0.5 h-4 w-4 shrink-0 text-sage" strokeWidth={3} />{x}</li>))}</ul></div>
              <div className="rounded-2xl border border-border bg-white p-5"><h3 className="font-display text-sm font-bold text-navy">Obligations of the recipient</h3><ul className="mt-3 space-y-2">{rg.rights.recipient.map((x, i) => (<li key={i} className="flex items-start gap-2 text-sm text-ink"><ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-navy" />{x}</li>))}</ul></div>
            </div>
            <div className="mt-4 rounded-2xl border border-gold/40 bg-gold/10 p-5"><h3 className="font-display text-sm font-bold text-goldDark">Important legal requirements</h3><ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">{rg.rights.requirements.map((x, i) => (<li key={i} className="flex items-start gap-2 text-sm text-ink"><FileSignature className="mt-0.5 h-4 w-4 shrink-0 text-goldDark" />{x}</li>))}</ul></div>
          </section>

          <section className="mx-auto max-w-5xl px-6 py-8 lg:px-10" data-testid="guide-info-needed">
            <h2 className="font-display text-2xl font-extrabold text-ink">What information you'll need (and where to find it)</h2>
            <div className="mt-6 overflow-hidden rounded-2xl border border-border">
              <table className="w-full border-collapse text-left text-sm">
                <thead><tr className="bg-night text-white"><th className="px-4 py-3 font-display font-bold">Information</th><th className="px-4 py-3 font-display font-bold">Why it's needed</th><th className="px-4 py-3 font-display font-bold">Where to find it</th></tr></thead>
                <tbody>{rg.infoNeeded.map((r, i) => (<tr key={i} className={i % 2 ? "bg-cream" : "bg-white"}><td className="px-4 py-3 align-top font-semibold text-ink">{r.field}</td><td className="px-4 py-3 align-top text-slate-600">{r.why}</td><td className="px-4 py-3 align-top text-slate-600">{r.where}<span className="mt-1 block text-xs text-slate-400">If unavailable: {r.ifUnavailable}</span></td></tr>))}</tbody>
              </table>
            </div>
          </section>

          {rg.relatedInline?.length > 0 && (
            <section className="mx-auto max-w-5xl px-6 pb-2 lg:px-10">
              <div className="rounded-2xl border border-navy/15 bg-navy/[0.03] p-5" data-testid="guide-inline-related">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-navy">Related legal notices &amp; guides</p>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {rg.relatedInline.map((r, i) => (
                    <Link key={i} to={`/legal-notice/${r.slug}`} data-testid={`inline-link-${r.slug}`} className="group flex items-start gap-2 rounded-xl border border-border bg-white p-3 transition-colors hover:border-navy/40">
                      <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-navy" />
                      <span><span className="text-sm font-semibold text-ink group-hover:text-navy">{r.title}</span><span className="block text-xs text-slate-500">{r.blurb}</span></span>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      )}

      {/* HOW IT WORKS */}
      <section className="bg-white py-14">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <div className="flex items-center gap-2 text-goldDark">
            <Sparkles className="h-4 w-4" />
            <span className="text-xs font-bold uppercase tracking-[0.18em]">How it works</span>
          </div>
          <h2 className="mt-2 font-display text-3xl font-black text-ink">From your story to a stamped notice</h2>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {HOW_IT_WORKS.map((step, i) => {
              const Icon = STEP_ICONS[i] || FileSignature;
              return (
                <div key={i} className="rounded-2xl border border-border bg-cream p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-white"><Icon className="h-5 w-5" /></span>
                  <p className="mt-4 text-xs font-bold text-goldDark">STEP {i + 1}</p>
                  <h3 className="mt-1 font-display text-lg font-extrabold text-ink">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{step.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY ZEXO */}
      <section className="mx-auto max-w-5xl px-6 py-14 lg:px-10">
        <h2 className="font-display text-3xl font-black text-ink">Why choose Zexo Legal</h2>
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {WHY_ZEXO.map((w, i) => (
            <div key={i} className="rounded-2xl border border-border bg-white p-6">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-navy/5 text-navy"><ShieldCheck className="h-5 w-5" /></span>
              <h3 className="mt-4 font-display text-lg font-extrabold text-ink">{w.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{w.body}</p>
            </div>
          ))}
        </div>
      </section>

      {notice.guide && (
        <section className="mx-auto max-w-5xl px-6 pt-14 lg:px-10" data-testid="guide-comparison">
          <h2 className="font-display text-3xl font-black text-ink">Zexo Legal vs a traditional lawyer</h2>
          <div className="mt-8 overflow-hidden rounded-2xl border border-border">
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="bg-night text-white">
                  <th className="px-5 py-4 font-display font-bold"> </th>
                  <th className="px-5 py-4 font-display font-bold">Zexo Legal</th>
                  <th className="px-5 py-4 font-display font-bold text-slate-300">Traditional lawyer</th>
                </tr>
              </thead>
              <tbody>
                {notice.guide.comparison.map((row, i) => (
                  <tr key={i} className={i % 2 ? "bg-cream" : "bg-white"}>
                    <td className="px-5 py-4 font-bold text-ink">{row.factor}</td>
                    <td className="px-5 py-4 align-top text-ink"><Check className="mr-1.5 inline h-4 w-4 text-sage align-[-2px]" strokeWidth={3} />{row.zexo}</td>
                    <td className="px-5 py-4 align-top text-slate-500">{row.trad}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* PRICING */}
      <section className="bg-night bg-noise py-14 text-white">
        <div className="mx-auto max-w-3xl px-6 text-center lg:px-10">
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-gold">Transparent pricing</span>
          <h2 className="mt-3 font-display text-3xl font-black">One clear fee. No surprises.</h2>
          <div className="mx-auto mt-8 max-w-md rounded-3xl border border-white/15 bg-white/5 p-8 backdrop-blur">
            <p className="font-display text-5xl font-black text-white">₹{price}</p>
            <p className="mt-2 text-sm text-slate-300">Base fee — drafting, advocate review, firm seal & email dispatch</p>
            <ul className="mt-6 space-y-2.5 text-left text-sm text-slate-200">
              {["Advocate reviews & stamps every notice", "Delivered by email, with a copy to you", "Optional WhatsApp, registered post & advocate call", "Track your notice anytime"].map((t, i) => (
                <li key={i} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-sage" /> {t}</li>
              ))}
            </ul>
            <button data-testid="pricing-generate-cta" onClick={goStart}
              className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-bold text-night shadow-lg transition-transform duration-200 hover:-translate-y-0.5 active:scale-95">
              Generate Legal Notice <ArrowRight className="h-4 w-4" />
            </button>
            <p className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-400"><Lock className="h-3.5 w-3.5" /> You won't be charged until you approve your draft.</p>
          </div>
        </div>
      </section>

      {notice.guide && (
        <section className="mx-auto max-w-5xl px-6 py-12 lg:px-10" data-testid="guide-next">
          <div className="rounded-3xl border border-border bg-white p-8">
            <div className="flex items-center gap-2 text-goldDark">
              <ArrowRight className="h-4 w-4" />
              <span className="text-xs font-bold uppercase tracking-[0.18em]">What happens next?</span>
            </div>
            <h2 className="mt-2 font-display text-2xl font-extrabold text-ink">Settlement &amp; next steps if it's ignored</h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">{notice.guide.whatHappensNext}</p>
          </div>
        </section>
      )}

      {rg && (
        <>
          <section className="mx-auto max-w-5xl px-6 py-8 lg:px-10" data-testid="guide-docs-detail">
            <h2 className="font-display text-2xl font-extrabold text-ink">Why each document matters</h2>
            <div className="mt-6 overflow-hidden rounded-2xl border border-border">
              <table className="w-full border-collapse text-left text-sm">
                <thead><tr className="bg-night text-white"><th className="px-4 py-3 font-display font-bold">Document</th><th className="px-4 py-3 font-display font-bold">Why it strengthens your notice</th><th className="px-4 py-3 font-display font-bold">Required?</th></tr></thead>
                <tbody>{rg.documents.map((d, i) => (<tr key={i} className={i % 2 ? "bg-cream" : "bg-white"}><td className="px-4 py-3 align-top font-semibold text-ink">{d.name}</td><td className="px-4 py-3 align-top text-slate-600">{d.why}</td><td className="px-4 py-3 align-top"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${d.mandatory ? "bg-destructive/10 text-destructive" : "bg-sage/15 text-sage"}`}>{d.mandatory ? "Mandatory" : "Optional"}</span></td></tr>))}</tbody>
              </table>
            </div>
          </section>

          <section className="mx-auto max-w-5xl px-6 py-8 lg:px-10" data-testid="guide-outcomes">
            <div className="flex items-center gap-2 text-goldDark"><ArrowRight className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-[0.18em]">What happens after you send</span></div>
            <h2 className="mt-2 font-display text-2xl font-extrabold text-ink">Every possible outcome</h2>
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
              {rg.outcomes.map((o, i) => (<div key={i} className="rounded-2xl border border-border bg-white p-5"><p className="font-display text-sm font-bold text-navy">{o.title}</p><p className="mt-2 text-sm leading-relaxed text-slate-600">{o.detail}</p></div>))}
            </div>
            <p className="mt-4 text-xs text-slate-400">Outcomes depend on the facts of each matter; no result is guaranteed.</p>
          </section>

          <section className="mx-auto max-w-5xl px-6 py-8 lg:px-10" data-testid="guide-mistakes">
            <div className="flex items-center gap-2 text-destructive"><AlertTriangle className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-[0.18em]">Common mistakes to avoid</span></div>
            <h2 className="mt-2 font-display text-2xl font-extrabold text-ink">Mistakes that weaken a notice — and how to avoid them</h2>
            <div className="mt-6 space-y-3">
              {rg.commonMistakes.map((m, i) => (
                <div key={i} className="rounded-2xl border border-border bg-white p-4">
                  <p className="flex items-start gap-2 text-sm font-semibold text-ink"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />{m.mistake}</p>
                  <p className="mt-1 flex items-start gap-2 pl-6 text-sm text-slate-600"><Check className="mt-0.5 h-4 w-4 shrink-0 text-sage" strokeWidth={3} />{m.fix}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mx-auto max-w-5xl px-6 py-8 lg:px-10" data-testid="guide-example">
            <h2 className="font-display text-2xl font-extrabold text-ink">A real-world example</h2>
            <div className="mt-6 space-y-3">
              {[["The problem", rg.example.problem], ["Notice sent", rg.example.notice], ["Recipient's response", rg.example.response], ["Resolution", rg.example.resolution]].map(([label, body], i, arr) => (
                <div key={i} className="relative rounded-2xl border border-border bg-white p-5">
                  <div className="flex items-center gap-2"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-navy text-xs font-bold text-white">{i + 1}</span><p className="font-display text-sm font-bold text-navy">{label}</p></div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
                  {i < arr.length - 1 && <div className="mt-1 flex justify-center text-slate-300"><ChevronRight className="h-4 w-4 rotate-90" /></div>}
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-slate-400">Illustrative scenario for guidance only — not an actual case or client.</p>
          </section>
        </>
      )}

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-6 py-14 lg:px-10">
        <div className="flex items-center gap-2 text-goldDark">
          <HelpCircle className="h-4 w-4" />
          <span className="text-xs font-bold uppercase tracking-[0.18em]">FAQs</span>
        </div>
        <h2 className="mt-2 font-display text-3xl font-black text-ink">Questions about this notice</h2>
        <Accordion type="single" collapsible className="mt-8" data-testid="faq-accordion">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left font-display text-base font-bold text-ink">{f.q}</AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-slate-600">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="mx-auto max-w-5xl px-6 pb-16 lg:px-10" data-testid="related-notices">
          <h2 className="font-display text-2xl font-extrabold text-ink">Related legal notices</h2>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {related.map((r) => (
              <Link key={r.slug} to={`/legal-notice/${r.slug}`} data-testid={`related-${r.slug}`}
                className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-white p-5 transition-colors hover:border-navy">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wide text-goldDark">{r.categoryTitle}</p>
                  <p className="mt-1 font-display text-base font-bold text-ink group-hover:text-navy">{r.title}</p>
                </div>
                <ArrowUpRight className="h-5 w-5 shrink-0 text-slate-400 transition-colors group-hover:text-navy" />
              </Link>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/legal-notices" className="inline-flex items-center gap-2 text-sm font-bold text-navy hover:underline">
              <Scale className="h-4 w-4" /> Browse all notice types
            </Link>
          </div>
        </section>
      )}

      <SiteFooter firmName={config.firm_name} />
    </div>
  );
}
